import { Button } from '@design-system';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { useContext, useEffect } from 'react';

import { AuthEntryPoint } from '../features/auth/types';
import { useIsSubscribed } from '../hooks/subscription';
import { useAuthenticate } from '../hooks/useAuthenticate';
import { useAuth } from '../providers';
import { AccountContext } from '../providers/AccountProvider';
import { StickySubscriptionCtaStoryblokExtended } from '../types/Franchises';
import { GeneratedSubscriptionType } from '../types/generated';
import { useCookieConsent } from '../hooks/useCookieConsent';
import { HideHubspot } from './HideHubspot';
import { useAnalyticsEmitter } from '@utils/analytics/emitter';
import { showPhoneStep } from '../helpers/phone';

export const ContentBlockStickySubscription = ({
  blok,
  isSectionsInView = false,
}: {
  blok: StickySubscriptionCtaStoryblokExtended;
  isSectionsInView?: boolean;
}) => {
  const { t } = useTranslation(['releases', 'authentication']);
  const [emitter] = useAnalyticsEmitter();
  const { user } = useAuth();
  const { onboarding } = useContext(AccountContext);

  const [cookieConsent] = useCookieConsent();
  const hasCookieBanner = cookieConsent === 'UNDECIDED';

  const { authenticateAndSubscribe } = useAuthenticate();
  const releaseSubscriptions = [
    {
      objectId: blok.subscriptionId,
      type: GeneratedSubscriptionType.Release,
      name: blok.subscriptionName || '',
      ref: '',
    },
  ];

  if (blok.HubspotListId) {
    releaseSubscriptions.push({
      objectId: blok.HubspotListId,
      type: GeneratedSubscriptionType.HubspotList,
      name: blok.HubspotListId || '',
      ref: '',
    });
  }

  const artistSubscriptions = blok.subscriptionArtists.map((artist) => {
    return {
      objectId: artist.id,
      type: GeneratedSubscriptionType.Artist,
      name: artist.name || '',
      ref: '',
    };
  });

  const blokSubscriptions = [...releaseSubscriptions, ...artistSubscriptions];

  const {
    isSubscribedAll: isSubscribed,
    loading,
    refetch,
  } = useIsSubscribed(blokSubscriptions);

  useEffect(() => {
    if (!user && isSubscribed) refetch();
  }, [isSubscribed, refetch, user]);

  const handleSubscribe = async () => {
    await authenticateAndSubscribe({
      authScreenThumbnail: '',
      authScreenTitle: t('authentication:dynamicTitle.product'),
      subscriptions: blokSubscriptions,
      analyticsProperties: {
        'artist name': blok.subscriptionArtists
          .map(({ name }) => name)
          .join(','),
        artist_id: blok.subscriptionArtists.map(({ id }) => id).join(','),
        button_location: 'release page',
        release_id: blok.subscriptionId,
        source: AuthEntryPoint.Release,
        subscription_name: blok.subscriptionName,
      },
      showOnboarding: !showPhoneStep(user),
      showPhoneStep: showPhoneStep(user),
      hideSuccessScreen: !onboarding?.isOnboardingStarted,
      onFlowComplete: () => {
        onboarding?.show({
          page: AuthEntryPoint.Release,
          id: blok.subscriptionId,
        });
      },
    });
    refetch();
  };

  const variant = blok.variant || 'default';
  const isLive = Boolean(blok.productListId && blok.releaseStatus === 'live');
  const copyKey = isLive ? 'live' : variant;

  return (
    <div
      className={classNames(
        'pointer-events-none fixed inset-x-0 z-30 px-5 pb-6 transition duration-300 ease-out',
        hasCookieBanner ? 'bottom-[70px]' : 'bottom-0',
        isSectionsInView
          ? 'translate-y-0 opacity-100'
          : 'translate-y-full opacity-0',
      )}
    >
      <div className="pointer-events-auto mx-auto flex w-full max-w-md items-center gap-3 overflow-hidden rounded-md border border-divider/20 bg-neutral-6 p-4 text-white">
        <div className="flex w-full flex-col overflow-hidden">
          <p className="truncate">
            {t(`releases:stickySubscription.${copyKey}.title`)}
          </p>
          <p className="truncate text-neutral-3">
            {t(`releases:stickySubscription.${copyKey}.subtitle`)}
          </p>
        </div>
        {isLive ? (
          <Button
            variant="secondary"
            size="md"
            as="a"
            href={`#${blok.productListId}`}
            label={t('releases:stickySubscription.live.cta')}
            className="shrink-0"
          />
        ) : (
          <Button
            disabled={isSubscribed}
            icon="ic_notification_outline"
            label={
              isSubscribed
                ? t('releases:stickySubscription.subscribed')
                : t(`releases:stickySubscription.${variant}.cta`)
            }
            loading={loading}
            onClick={() => {
              emitter('', 'button clicked', {
                label: isSubscribed
                  ? t('releases:stickySubscription.subscribed')
                  : t(`releases:stickySubscription.${variant}.cta`),
                button_location: 'sticky cta',
              });
              handleSubscribe();
            }}
            size="md"
            type="button"
            variant={isSubscribed ? 'positive' : 'secondary'}
          />
        )}
      </div>
      <HideHubspot />
    </div>
  );
};
